import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AnswerData, QuizQuestionData } from 'quiz-types';

interface QUIZ_BUILDER_STATE_TYPE {
  id: string;
  name: string;
  questions: Array<QuizQuestionData>;
  answers: Array<AnswerData>;
  activeQuestionIndex: number;
  attempts: number | null;
  dueDate: Date | null;
  lockQuiz: boolean;
  // Current quiz being taken (for flashcard generation)
  currentQuizQuestions: Array<QuizQuestionData>;
  // Flag to track if quiz was just submitted in current session
  justSubmitted: boolean;
  blocksDownstream: boolean;
  requiresMinimumGrade: boolean;
  minimumGradeThreshold: number | null;
}

const INITIAL_STATE: QUIZ_BUILDER_STATE_TYPE = {
  id: '',
  name: 'Untitled Quiz',
  questions: [
    { question: '', type: 'Multiple Choice', index: 0, options: [{ index: 0, value: '' }] },
  ],
  answers: [],
  activeQuestionIndex: 0,
  attempts: null,
  dueDate: null,
  lockQuiz: false,
  currentQuizQuestions: [],
  justSubmitted: false,
  blocksDownstream: false,
  requiresMinimumGrade: false,
  minimumGradeThreshold: null,
};

const quizBuilderSlice = createSlice({
  name: 'quiz-builder',
  initialState: INITIAL_STATE,
  reducers: {
    setQuizId(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<string>) {
      state.id = action.payload;
    },
    setQuizName(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    setQuizData(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<Array<QuizQuestionData>>) {
      state.questions = action.payload;
    },
    setQuizAllowedAttempts(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<number | null>) {
      state.attempts = action.payload;
    },
    setQuizDueDate(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<Date | null>) {
      state.dueDate = action.payload;
    },
    setQuizLockQuiz(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<boolean>) {
      state.lockQuiz = action.payload;
    },
    updateQuestion(
      state,
      action: PayloadAction<{ index: number; question: Partial<QuizQuestionData> }>,
    ) {
      const { index, question } = action.payload;
      state.questions[index] = { ...state.questions[index], ...question };
    },
    addQuestion(
      state: QUIZ_BUILDER_STATE_TYPE,
      action: PayloadAction<{ index: number; question: QuizQuestionData }>,
    ) {
      const { index, question } = action.payload;
      state.questions.splice(index, 0, question);
      state.questions = state.questions.map((q, i) => ({ ...q, index: i }));
    },
    deleteQuizDataOnly(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<number>) {
      state.questions = state.questions
        .filter((question) => question.index !== action.payload)
        .map((question, index) => ({ ...question, index }));
    },
    setAnswerData(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<Array<AnswerData>>) {
      state.answers = action.payload;
    },
    deleteQuestionAndUpdateAnswers(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<number>) {
      const indexToDelete = action.payload;
      let newIndex = indexToDelete - 1;
      if (indexToDelete === 0) {
        newIndex = 0;
      }

      state.questions.splice(indexToDelete, 1);
      state.questions.forEach((q, idx) => (q.index = idx));
      state.answers = state.answers
        .filter((answer) => answer.questionIndex !== indexToDelete)
        .map((answer) => ({
          ...answer,
          questionIndex:
            answer.questionIndex > indexToDelete ? answer.questionIndex - 1 : answer.questionIndex,
        }));
      state.activeQuestionIndex = newIndex;
    },
    swapToAnswerOptions(state, action: PayloadAction<number>) {
      state.answers = state.answers.filter((answer) => answer.questionIndex != action.payload);
      state.answers.push({ questionIndex: action.payload, answerData: [] });
    },
    updateAnswerKeyOptions(state, action: PayloadAction<{ index: number; answer: AnswerData }>) {
      const { index, answer } = action.payload;

      if (state.answers.some((entry) => entry.questionIndex == index)) {
        state.answers = [
          ...state.answers.map((entry) => (entry.questionIndex == index ? answer : entry)),
        ];
        return;
      }
      state.answers.push(answer);
    },
    swapToAnswerSA(state, action: PayloadAction<number>) {
      state.answers = state.answers.filter((answer) => answer.questionIndex != action.payload);
      state.answers.push({ questionIndex: action.payload, answerData: '' });
    },
    updateAnswerKeySA(state, action: PayloadAction<{ index: number; answer: string }>) {
      const { index, answer } = action.payload;

      if (answer === '') {
        // Remove the answer entry if the answer string is empty
        state.answers = state.answers.filter((entry) => entry.questionIndex !== index);
      } else {
        // Check if an answer already exists for this question
        const existingAnswerIndex = state.answers.findIndex(
          (entry) => entry.questionIndex === index,
        );
        if (existingAnswerIndex !== -1) {
          // Update the existing answer
          state.answers[existingAnswerIndex].answerData = answer;
        } else {
          // Add a new answer if it does not exist
          state.answers.push({ questionIndex: index, answerData: answer });
        }
      }
    },
    setActiveQuestionIndex(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<number>) {
      state.activeQuestionIndex = action.payload;
    },
    resetQuizData(state: QUIZ_BUILDER_STATE_TYPE) {
      state.name = INITIAL_STATE.name;
      state.questions = INITIAL_STATE.questions;
      state.activeQuestionIndex = INITIAL_STATE.activeQuestionIndex;
      state.answers = INITIAL_STATE.answers;
      state.attempts = INITIAL_STATE.attempts;
      state.dueDate = INITIAL_STATE.dueDate;
      state.lockQuiz = INITIAL_STATE.lockQuiz;
      state.currentQuizQuestions = INITIAL_STATE.currentQuizQuestions;
      state.justSubmitted = INITIAL_STATE.justSubmitted;
      state.blocksDownstream = INITIAL_STATE.blocksDownstream;
      state.requiresMinimumGrade = INITIAL_STATE.requiresMinimumGrade;
      state.minimumGradeThreshold = INITIAL_STATE.minimumGradeThreshold;
    },
    setCurrentQuizQuestions(
      state: QUIZ_BUILDER_STATE_TYPE,
      action: PayloadAction<Array<QuizQuestionData>>,
    ) {
      state.currentQuizQuestions = action.payload;
    },
    setQuizJustSubmitted(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<boolean>) {
      state.justSubmitted = action.payload;
    },
    setBlocksDownstream(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<boolean>) {
      state.blocksDownstream = action.payload;
    },
    setRequiresMinimumGrade(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<boolean>) {
      state.requiresMinimumGrade = action.payload;
    },
    setMinimumGradeThreshold(state: QUIZ_BUILDER_STATE_TYPE, action: PayloadAction<number | null>) {
      state.minimumGradeThreshold = action.payload;
    },
  },
});

export const {
  setQuizId,
  setQuizName,
  setQuizData,
  setQuizAllowedAttempts,
  setQuizDueDate,
  setQuizLockQuiz,
  updateQuestion,
  addQuestion,
  deleteQuizDataOnly,
  setActiveQuestionIndex,
  setAnswerData,
  swapToAnswerOptions,
  updateAnswerKeyOptions,
  swapToAnswerSA,
  updateAnswerKeySA,
  resetQuizData,
  deleteQuestionAndUpdateAnswers,
  setCurrentQuizQuestions,
  setQuizJustSubmitted,
  setBlocksDownstream,
  setRequiresMinimumGrade,
  setMinimumGradeThreshold,
} = quizBuilderSlice.actions;

export default quizBuilderSlice.reducer;
